/**
 * Audio manifest loader — slice 1 of the audio runtime (docs/design/18-audio.md).
 *
 * The sound registry (`@tribez/shared` → audio.ts) references logical asset
 * keys, never file paths. This manifest is the one place that resolves a key →
 * served URL(s), so relocating bytes (CF Pages → R2) is a manifest swap with
 * zero code change. v1 ships AAC-only (`.m4a`); extra formats are additive —
 * Howler picks the first playable URL from the array we hand it.
 */

/** Format priority — Howler tries these in order and uses the first it can play. */
const FORMAT_ORDER = ["m4a", "webm", "ogg", "mp3"] as const;
type AudioFormat = (typeof FORMAT_ORDER)[number];

export interface AudioManifest {
  version: number;
  assets: Record<string, Partial<Record<AudioFormat, string>>>;
}

export interface ResolvedAsset {
  /** URLs in preference order (first = most-preferred format). */
  urls: string[];
  /** Matching format hints (`howler` wants these when the URL has no clean ext). */
  formats: string[];
}

const DEFAULT_URL = "/assets/audio/audio-manifest.json";

/** Fetch the manifest once at boot. Throws on network/parse failure so the
 *  caller can decide (we currently log + run silent rather than crash). */
export async function loadAudioManifest(url: string = DEFAULT_URL): Promise<AudioManifest> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`audio manifest fetch failed: ${res.status} ${res.statusText}`);
  const json = (await res.json()) as AudioManifest;
  if (!json || typeof json !== "object" || !json.assets) {
    throw new Error("audio manifest malformed: missing `assets`");
  }
  return json;
}

/** Resolve a manifest key → playable URLs, or `undefined` if absent (→ silent
 *  no-op upstream — the data-driven-deps contract: a row can exist before its
 *  file lands). */
export function resolveAsset(manifest: AudioManifest, key: string): ResolvedAsset | undefined {
  const entry = manifest.assets[key];
  if (!entry) return undefined;
  const urls: string[] = [];
  const formats: string[] = [];
  for (const fmt of FORMAT_ORDER) {
    const u = entry[fmt];
    if (u) {
      urls.push(u);
      formats.push(fmt);
    }
  }
  return urls.length ? { urls, formats } : undefined;
}
